import { API_URL, apiCall } from '../config/api';
import { getAuthHeader } from '../config/auth';
import type { CampaignResources, CampaignWorkspace, CampaignWorkItem, CampaignWorkItemObservation } from './types';
import { cancelAgentTurn, fetchAgentConversationHistory, openAgentChatStream } from '../agent-surfaces/core/conversationApi';
import { parseTurnLifecycleEvent, readAgentStreamEvents } from '../agent-surfaces/core/streamEvents';
import type { DurableHistoryMessage } from '../agent-surfaces/core/lifecycleHistory';

export async function listCampaignWorkspaces(): Promise<CampaignWorkspace[]> {
  return (await apiCall('/api/campaign-workspaces')) || [];
}

export async function createCampaignWorkspace(
  title: string,
  idempotencyKey: string,
): Promise<CampaignWorkspaceCreateResult> {
  try {
    const response = await fetch(`${API_URL}/api/campaign-workspaces`, {
      method: 'POST',
      headers: {
        Authorization: getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ title, idempotency_key: idempotencyKey }),
    });
    if (!response.ok) {
      let code = `http_${response.status}`;
      try {
        const payload = await response.json();
        if (typeof payload?.detail === 'string') code = payload.detail;
      } catch {
        // The HTTP rejection itself is still confirmed even without JSON detail.
      }
      return { status: 'rejected', httpStatus: response.status, code };
    }
    const workspace = await response.json();
    if (!workspace?.id) return { status: 'unknown' };
    return { status: 'created', workspace };
  } catch {
    // Transport loss or an unreadable success body cannot prove whether the
    // idempotent server transaction committed.
    return { status: 'unknown' };
  }
}

export type CampaignWorkspaceCreateResult =
  | { status: 'created'; workspace: CampaignWorkspace }
  | { status: 'rejected'; httpStatus: number; code: string }
  | { status: 'unknown' };

export async function fetchCampaignWorkspace(workspaceId: string): Promise<CampaignWorkspace | null> {
  return apiCall(`/api/campaign-workspaces/${workspaceId}`);
}

export type CampaignWorkspaceRenameResult =
  | { status: 'updated'; workspace: CampaignWorkspace }
  | { status: 'conflict'; workspace: CampaignWorkspace }
  | { status: 'conflict_refresh_failed' }
  | { status: 'error' };

export async function renameCampaignWorkspace(
  workspaceId: string,
  title: string,
  expectedVersion: number,
): Promise<CampaignWorkspaceRenameResult> {
  try {
    const response = await fetch(
      `${API_URL}/api/campaign-workspaces/${encodeURIComponent(workspaceId)}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title, expected_version: expectedVersion }),
      },
    );
    if (response.status === 409) {
      let workspace: CampaignWorkspace | null = null;
      try {
        workspace = await fetchCampaignWorkspace(workspaceId);
      } catch {
        return { status: 'conflict_refresh_failed' };
      }
      if (!workspace) return { status: 'conflict_refresh_failed' };
      return {
        status: 'conflict',
        workspace,
      };
    }
    if (!response.ok) return { status: 'error' };
    const workspace = await response.json();
    if (!workspace?.id) return { status: 'error' };
    return { status: 'updated', workspace };
  } catch {
    return { status: 'error' };
  }
}

export async function fetchCampaignResources(workspaceId: string): Promise<CampaignResources> {
  return (await apiCall(`/api/campaign-workspaces/${workspaceId}/resources`)) || {
    assets: [], evidence: [],
  };
}

export async function refreshCampaignArtifactSummary(
  workspaceId: string,
  artifactLinkId: string,
): Promise<Record<string, unknown> | null> {
  return apiCall(`/api/campaign-workspaces/${workspaceId}/artifact-links/${artifactLinkId}/summary`, {
    method: 'POST',
  });
}

export async function fetchCampaignEvidencePartBlob(
  workspaceId: string,
  partId: string,
  signal?: AbortSignal,
): Promise<Blob | null> {
  const response = await fetch(
    `${API_URL}/api/campaign-workspaces/${encodeURIComponent(workspaceId)}/evidence-parts/${encodeURIComponent(partId)}/content`,
    {
      method: 'GET',
      headers: { Authorization: getAuthHeader() },
      signal,
    },
  );
  if (!response.ok) return null;
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.startsWith('image/')) return null;
  return response.blob();
}

export async function listCampaignWorkItems(workspaceId: string): Promise<CampaignWorkItem[]> {
  const response = await apiCall(`/api/campaign-workspaces/${workspaceId}/work-items`);
  // `apiCall` uses null for HTTP, transport, and decoding failures. Treating
  // that sentinel as an empty queue makes the wake loop conclude inspection
  // succeeded and release persistence while the campaign state is unknown.
  // A real empty queue is already represented by `[]` and remains valid.
  if (!Array.isArray(response)) {
    throw new Error('campaign_work_items_not_confirmed');
  }
  return response;
}

export async function observeCampaignWorkItems(workspaceId: string): Promise<CampaignWorkItemObservation> {
  const response = await apiCall(`/api/campaign-workspaces/${workspaceId}/work-items/observation`);
  if (
    !response || typeof response !== 'object' || !Array.isArray(response.items)
    || typeof response.reconcile_due !== 'boolean'
    || !response.items.every((item: unknown) => (
      item !== null && typeof item === 'object'
      && typeof (item as { id?: unknown }).id === 'string'
      && typeof (item as { status?: unknown }).status === 'string'
      && typeof (item as { version?: unknown }).version === 'number'
    ))
  ) {
    throw new Error('campaign_work_item_observation_not_confirmed');
  }
  return response as CampaignWorkItemObservation;
}

export async function settleCampaignWorkItemFromCurrentArtifact(
  workspaceId: string,
  workItemId: string,
): Promise<CampaignWorkItem | null> {
  const settled = await apiCall(`/api/campaign-workspaces/${workspaceId}/work-items/${workItemId}/settle-current-artifact`, {
    method: 'POST',
    // A 409 is the normal first poll while the native browser-applied change
    // is still being durably saved. The caller retries it; it is not a dev
    // console failure or a user-visible error.
    suppressErrorStatuses: [409],
  });
  // The shared API helper deliberately normalizes both non-2xx responses and
  // transport failures to null. Settlement polling must distinguish that
  // from a confirmed server response or it will stop before the native
  // debounced artifact save lands.
  if (!settled) throw new Error('campaign_artifact_settlement_not_confirmed');
  if (settled.status === 'dispatched') {
    // The native request never reached the server-side claim boundary. This
    // is retryable as a native invocation, not an artifact-settlement wait.
    throw new Error('campaign_work_item_not_claimed');
  }
  if (settled.status === 'running' || typeof settled.status !== 'string') {
    throw new Error('campaign_artifact_settlement_not_confirmed');
  }
  return settled;
}

export async function reportCampaignWorkItemInvocationFailure(
  workspaceId: string,
  workItemId: string,
): Promise<CampaignWorkItem> {
  const reported = await apiCall(
    `/api/campaign-workspaces/${workspaceId}/work-items/${workItemId}/report-browser-invocation-failure`,
    { method: 'POST' },
  );
  if (
    !reported
    || reported.status !== 'blocked'
    || reported.browser_invocation_failure_recorded !== true
  ) {
    throw new Error('campaign_invocation_failure_not_recorded');
  }
  return reported;
}

export async function fetchCampaignCoordinatorHistory(
  coordinatorConversationId: string,
): Promise<DurableHistoryMessage[]> {
  const response = await fetchAgentConversationHistory(coordinatorConversationId);
  return Array.isArray(response?.messages) ? response.messages : [];
}

export async function createCampaignWorkItem(input: {
  workspaceId: string;
  targetAgentSessionId: string;
  objective: string;
  expectedContextVersion: number;
  idempotencyKey: string;
}): Promise<CampaignWorkItem | null> {
  return apiCall(`/api/campaign-workspaces/${input.workspaceId}/work-items`, {
    method: 'POST',
    body: JSON.stringify({
      target_agent_session_id: input.targetAgentSessionId,
      objective: input.objective,
      expected_context_version: input.expectedContextVersion,
      idempotency_key: input.idempotencyKey,
    }),
  });
}

export async function sendCampaignCoordinatorMessage(input: {
  workspaceId: string;
  coordinatorConversationId: string;
  message: string;
  targetAgentSessionId?: string;
  targetContextVersion?: number;
  metadata?: Record<string, unknown>;
  onTurnStarted?: (turn: CoordinatorTurnIdentity) => void;
}): Promise<{ reply: string } | null> {
  const metadata: Record<string, unknown> = {
    ...input.metadata,
    campaign_workspace_id: input.workspaceId,
  };
  if (input.targetAgentSessionId && input.targetContextVersion !== undefined) {
    metadata.campaign_target_agent_session_id = input.targetAgentSessionId;
    metadata.campaign_target_context_version = input.targetContextVersion;
  }
  const reader = await openAgentChatStream({
    message: input.message,
    conversation_id: input.coordinatorConversationId,
    interaction_mode: 'campaign_coordinator',
    metadata,
  });
  return readCoordinatorResult(reader, input.onTurnStarted);
}

/** Server identity of a running Niki turn: what an explicit Stop names. */
export interface CoordinatorTurnIdentity {
  requestId: string;
  turnId: string | null;
}

export interface CampaignCoordinatorContinuationState {
  pending_work_item_ids: string[];
  in_flight: boolean;
  /** The turn running on the coordinator conversation, if this server has one. */
  active_turn: CoordinatorTurnIdentity | null;
}

/** Read-only: settled specialist outcomes the server still owes Niki. */
export async function fetchCampaignCoordinatorContinuation(
  workspaceId: string,
): Promise<CampaignCoordinatorContinuationState | null> {
  const response = await apiCall(`/api/campaign-workspaces/${workspaceId}/coordinator-continuation`);
  if (!response || !Array.isArray(response.pending_work_item_ids)) return null;
  const active = response.active_turn;
  return {
    pending_work_item_ids: response.pending_work_item_ids,
    in_flight: response.in_flight === true,
    active_turn: active && typeof active.request_id === 'string' && active.request_id
      ? { requestId: active.request_id, turnId: typeof active.turn_id === 'string' ? active.turn_id : null }
      : null,
  };
}

/**
 * Start one automatic Niki turn. The server claims the owed outcomes and
 * authors the message; this sends no text. A 409 means a user turn or another
 * tab owns the conversation, and the outcomes stay owed.
 */
export async function runCampaignCoordinatorContinuation(input: {
  workspaceId: string;
  coordinatorConversationId: string;
  onTurnStarted?: (turn: CoordinatorTurnIdentity) => void;
}): Promise<{ reply: string } | null> {
  const reader = await openAgentChatStream({
    message: '',
    conversation_id: input.coordinatorConversationId,
    interaction_mode: 'campaign_coordinator',
    metadata: { campaign_workspace_id: input.workspaceId },
    coordinator_continuation: true,
  });
  return readCoordinatorResult(reader, input.onTurnStarted);
}

/**
 * Explicit Stop for a Niki turn, user-started or automatic. Only a user's Stop
 * click calls this; closing or reloading the page never does, so a disconnect
 * is never recorded as the user's decision.
 */
export async function stopCampaignCoordinatorTurn(turn: {
  conversationId: string;
  requestId: string;
  turnId?: string | null;
}) {
  return cancelAgentTurn({
    conversation_id: turn.conversationId,
    request_id: turn.requestId,
    ...(turn.turnId ? { turn_id: turn.turnId } : {}),
  });
}

async function readCoordinatorResult(
  reader: Awaited<ReturnType<typeof openAgentChatStream>>,
  onTurnStarted?: (turn: CoordinatorTurnIdentity) => void,
): Promise<{ reply: string } | null> {
  for await (const event of readAgentStreamEvents(reader)) {
    if (event.type === 'turn_lifecycle') {
      const lifecycle = parseTurnLifecycleEvent(event.data);
      if (lifecycle && onTurnStarted) {
        onTurnStarted({ requestId: lifecycle.request_id, turnId: lifecycle.turn_id });
      }
      continue;
    }
    if (event.type === 'error') {
      throw new Error(String((event as any)?.data?.message || 'Campaign coordinator failed'));
    }
    if (event.type === 'result') {
      const data = (event as any).data || {};
      const reply = String(data.reply || '');
      return {
        reply: reply.startsWith('The Visual runtime encountered an error')
          ? reply.replace('The Visual runtime', 'The agent runtime')
          : reply,
      };
    }
  }
  return null;
}
